import { useCallback, useMemo } from 'react'

import { isFunction } from 'lodash'
import { useSelector } from 'react-redux'

import { isOptimistic } from '../..'
import { createInstitutionConstraint, deleteInstitutionConstraint, updateInstitutionConstraint } from '../institutionConstraints'
import { Actions, toItemActionPairs } from '../../events'
import { selectUser } from '../../../reducers/UserReducer'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { onError, onSuccess } from '../../apiHelper'
import { InstitutionConstraint } from '../../entities/institutionConstraints'
import { Properties } from '../../entities'

const useInstitutionConstraint = ({ callback }) => {
  const user = useSelector(selectUser)
  const t = useSelector(selectTranslate)

  const safeCallback = useMemo(() => isFunction(callback) ? callback : () => {}, [callback])

  const upsert = useCallback((constraint, previousState) => {
    const config = isOptimistic(constraint) ? { request: createInstitutionConstraint, action: Actions.CREATE } : { request: updateInstitutionConstraint, action: Actions.UPDATE }

    safeCallback(constraint, config.action)

    return config.request(user, constraint).then(response => {
      const newInstitutionConstraint = new InstitutionConstraint(response.data)

      if (config.action === Actions.CREATE) {
        safeCallback(constraint, Actions.DELETE, newInstitutionConstraint, Actions.CREATE)
      } else {
        safeCallback(newInstitutionConstraint, Actions.UPDATE)
      }

      onSuccess(t(`institution_constraint.${config.action.toLowerCase()}.success`))
    }).catch(reason => {
      console.error(reason)

      config.action === Actions.CREATE ? safeCallback(constraint, Actions.DELETE) : safeCallback(previousState, Actions.UPDATE)

      onError(t(`institution_constraint.${config.action.toLowerCase()}.error`))
    })
  }, [t, user, safeCallback])

  const del = useCallback(constraint => {
    return deleteInstitutionConstraint(user, constraint).then(({ data }) => {
      safeCallback(...toItemActionPairs(data, Actions.DELETE))

      onSuccess(t('institution_constraint.delete.success'))
    }).catch(error => {
      console.error(error)
      onError(t('institution_constraint.delete.error'))
    })
  }, [t, user, safeCallback])

  const toggle = useCallback(constraint => {
    const toggledConstraint = new InstitutionConstraint({ ...constraint, [Properties.ENABLE]: !constraint[Properties.ENABLE] })

    return updateInstitutionConstraint(user, toggledConstraint).then(({ data }) => {
      safeCallback(data, Actions.UPDATE)

      onSuccess(t('institution_constraint.update.success'))
    }).catch(reason => {
      console.error(reason)
      onError(t('institution_constraint.update.error'))
    })
  }, [t, user, safeCallback])


  return { upsert, delete: del, toggle }
}

export default useInstitutionConstraint